import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Button, Checkbox } from "..";
import {
  ModalActionContainer,
  ModalContainer,
  ModalContentWrapper,
  ModalBody,
  ModalHeader,
} from "../Modal/Modal.styles";
import { CheckboxRow } from "./RejoinQueueDialog.styles";
import { UserBookingInterest } from "../../models/BookingInterest";
import { rejoinQueue } from "../../services/BookingInterestV2Service";

/**
 * Second chance rejoin dialog (TTT-687 §4.6 / §5.6 / §10).
 *
 * Two entry points share this dialog:
 *  - a timed-out ticket offer  -> rejoins the queue
 *  - a missed check-in         -> joins the last min queue
 *
 * The "I understand" tickbox is mandatory (§2.4). Dependents can be kept or removed here,
 * never added (§2.4); adult guests are not carried over - they rejoin from their own
 * booking, so the copy nudges the user to remind them.
 */

export type RejoinVariant = "offerTimedOut" | "checkInMissed";

interface RejoinQueueDialogProps {
  open: boolean;
  variant: RejoinVariant;
  bookingInterest: UserBookingInterest;
  onClose: () => void;
  onCompleted?: (successful: boolean, message: string) => void;
}

// `accent` is rendered in the header's highlight colour, matching the other modals
// ("Please Remember! You've Agreed:", "Important! Before you proceed:").
const copy = {
  offerTimedOut: {
    accent: "You missed",
    title: "your ticket offer",
    body: "Your ticket offer expired before it was accepted. You can rejoin the queue for another chance if tickets become available.",
    disclaimer: "Rejoining does not guarantee another ticket.",
    cta: "Rejoin Queue",
  },
  checkInMissed: {
    accent: "You missed",
    title: "check-in",
    body: "Your ticket was released because check-in was not completed in time. You can rejoin the last minute queue for another chance if tickets become available.",
    disclaimer: "Tickets are limited and not guaranteed.",
    cta: "Join Last Min Queue",
  },
};

export const RejoinQueueDialog = ({
  open,
  variant,
  bookingInterest,
  onClose,
  onCompleted,
}: RejoinQueueDialogProps) => {
  const theme = useTheme();
  const texts = copy[variant];

  // The shared Checkbox draws no box of its own when unchecked — it relies on its fill
  // standing out from the page. On a modal that fill (neutral.900) is the paper colour, so
  // the default variant is literally invisible here. `secondary` swaps in the darker
  // neutral.950 and the outline makes it read as a control rather than a shadow.
  const checkboxSx = {
    border: `1px solid ${theme.colors["neutral.700"]}`,
    "&.MuiButtonBase-root.MuiCheckbox-root.isChecked": {
      borderColor: theme.colors["lime.500"],
    },
  };

  const dependents = (bookingInterest.relatedBookingInterests ?? []).filter((related) => related.isDependent);
  const hasAdultGuests = (bookingInterest.relatedBookingInterests ?? []).some((related) => !related.isDependent);

  const [acknowledged, setAcknowledged] = React.useState(false);
  const [keptDependentIds, setKeptDependentIds] = React.useState<number[]>(
    dependents.map((dependent) => dependent.id!).filter(Boolean)
  );
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string>("");

  React.useEffect(() => {
    if (open) {
      setAcknowledged(false);
      setError("");
      setKeptDependentIds(dependents.map((dependent) => dependent.id!).filter(Boolean));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleDependent = (dependentId: number) => {
    setKeptDependentIds((current) =>
      current.includes(dependentId)
        ? current.filter((id) => id !== dependentId)
        : [...current, dependentId]
    );
  };

  const handleSubmit = async () => {
    if (!bookingInterest.id) {
      return;
    }

    setLoading(true);
    setError("");

    const result = await rejoinQueue(bookingInterest.id, acknowledged, keptDependentIds);

    setLoading(false);

    if (!result.success) {
      setError(result.error ?? "Unable to rejoin the queue.");
      return;
    }

    onClose();
    // The API words the confirmation, because the two windows it quotes ("up to 12 hours
    // before check-in…") are the session's own admin-editable settings and are not on this
    // booking model. The fallback names no numbers rather than quoting defaults that may not
    // be this session's.
    onCompleted?.(
      true,
      result.message ??
        "You're back in the queue 🎉 If another ticket becomes available you'll be notified by email, or by SMS if it comes up once check-in has started. Because this is a second chance offer you'll have less time to accept, so make sure you're ready."
    );
  };

  return (
    <ModalContainer
      data-cy="rejoin-queue-dialog"
      PaperProps={{ elevation: 0 }}
      open={open}
      onClose={onClose}
      sx={{
        // Same treatment as the TermsHighlight / AccessibilityRequirementsChange modals,
        // applied here rather than in Modal.variants because this dialog is driven by its
        // own props (booking, variant, callbacks) instead of the global Modals enum.
        ".MuiPaper-root": {
          width: "36.375rem",
          gap: "1rem",
          border: `0.25rem solid ${theme.colors["orange.500"]}`,
          maxHeight: "fit-content",
        },
        ".modal-header > span": {
          color: theme.colors["orange.500"],
        },
      }}
    >
      <ModalHeader data-cy="rejoin-queue-title" className="modal-header">
        <span>{texts.accent}</span> {texts.title}
      </ModalHeader>

      {/* ModalContentWrapper declares flexDirection/gap but not display, which the
          single-child modals never notice; this dialog stacks several blocks. */}
      <ModalContentWrapper className="modal-content-wrapper" sx={{ display: "flex" }}>
        <ModalBody className="modal-body" data-cy="rejoin-queue-body">
          {texts.body}
        </ModalBody>

        <ModalBody className="modal-body" sx={{ fontSize: theme.fontSizes.sm }}>
          Your place in the queue is based on when you originally applied &mdash; not on when you rejoin.
        </ModalBody>

        {dependents.length > 0 && (
          <Box>
            <Typography
              sx={{
                color: theme.colors["neutral.50"],
                fontWeight: theme.fontWeights.semibold,
                fontSize: theme.fontSizes.sm,
                marginBottom: theme.space._100,
              }}
            >
              Who are you bringing?
            </Typography>
            <ModalBody className="modal-body" sx={{ fontSize: theme.fontSizes.sm, marginBottom: theme.space._200 }}>
              You can remove someone here, but you cannot add new guests.
            </ModalBody>
            {dependents.map((dependent) => (
              <CheckboxRow key={`rejoin-dependent-${dependent.id}`}>
                <Checkbox
                  data-cy="rejoin-dependent-option"
                  variant="secondary"
                  size="sm"
                  sx={checkboxSx}
                  checked={keptDependentIds.includes(dependent.id!)}
                  onChange={() => toggleDependent(dependent.id!)}
                  label={`${dependent.assignedFirstName ?? dependent.user?.firstName ?? ""} ${dependent.assignedLastName ?? dependent.user?.lastName ?? ""}`.trim()}
                />
              </CheckboxRow>
            ))}
          </Box>
        )}

        {hasAdultGuests && (
          <ModalBody className="modal-body" sx={{ fontSize: theme.fontSizes.sm }} data-cy="rejoin-remind-guests">
            Your guests aged 18+ need to rejoin from their own account &mdash; they got the same email.
            Give them a nudge if they still want to come.
          </ModalBody>
        )}

        <CheckboxRow>
          <Checkbox
            data-cy="rejoin-acknowledge-checkbox"
            variant="secondary"
            size="sm"
            sx={checkboxSx}
            checked={acknowledged}
            onChange={(checked) => setAcknowledged(checked)}
            label={`I understand. ${texts.disclaimer}`}
          />
        </CheckboxRow>

        {error && (
          <ModalBody
            className="modal-body"
            data-cy="rejoin-queue-error"
            sx={{ color: theme.colors["red.400"], fontSize: theme.fontSizes.sm }}
          >
            {error}
          </ModalBody>
        )}
      </ModalContentWrapper>

      <ModalActionContainer className="modal-button-container">
        <Button variant="tertiary" onClick={onClose} data-cy="rejoin-queue-cancel">
          Cancel
        </Button>
        <Button
          gradientStyle="gradient3"
          disabled={!acknowledged}
          loading={loading}
          onClick={handleSubmit}
          data-cy="rejoin-queue-submit"
        >
          {texts.cta}
        </Button>
      </ModalActionContainer>
    </ModalContainer>
  );
};

RejoinQueueDialog.displayName = "RejoinQueueDialog";
